import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Breadcrumbs,
  Link,
  IconButton,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment
} from '@mui/material';
import { SEO } from '../components/SEO/SEO';
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  Home as HomeIcon,
  RocketLaunch,
  AutoAwesome,
  Insights,
  Help,
  Tune,
  AdminPanelSettings,
  BugReport
} from '@mui/icons-material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { DocsSidebar } from '../components/docs/DocsSidebar';
import { DocArticle } from '../components/docs/DocArticle';
import { DocTableOfContents } from '../components/docs/DocTableOfContents';
import { useCurrentUser } from '../hooks/useApi';

// Icon map for category cards
const iconMap: Record<string, React.ElementType> = {
  RocketLaunch,
  AutoAwesome,
  Insights,
  Help,
  Tune,
  AdminPanelSettings,
  BugReport
};

interface Article {
  id: string;
  title: string;
  path: string;
  description: string;
  tags: string[];
  order: number;
}

interface Category {
  id: string;
  title: string;
  description: string;
  icon: string;
  requiresAuth: boolean;
  requiresPermission: string | null;
  order: number;
  articles: Article[];
}

interface Metadata {
  version: string;
  lastUpdated: string;
  categories: Category[];
}

export const DocsPage: React.FC = () => {
  const { categoryId, articleId } = useParams<{ categoryId?: string; articleId?: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { data: user } = useCurrentUser();

  const [metadata, setMetadata] = useState<Metadata | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentMarkdown, setCurrentMarkdown] = useState('');

  // Load metadata
  useEffect(() => {
    const loadMetadata = async () => {
      try {
        const response = await fetch('/docs/metadata.json');
        const data = await response.json();
        setMetadata(data);
      } catch (error) {
        console.error('Error loading metadata:', error);
      }
    };

    loadMetadata();
  }, []);

  // Filter categories based on user permissions
  const visibleCategories = metadata?.categories.filter(category => {
    if (category.requiresPermission) {
      return user?.permissions?.includes(category.requiresPermission);
    }
    if (category.requiresAuth) {
      return !!user;
    }
    return true;
  }) || [];

  // Find current category and article
  const currentCategory = visibleCategories.find(cat => cat.id === categoryId);
  const currentArticle = currentCategory?.articles.find(art => art.id === articleId);

  // Load markdown content for TOC
  useEffect(() => {
    const loadMarkdown = async () => {
      if (!currentArticle) {
        setCurrentMarkdown('');
        return;
      }

      try {
        const response = await fetch(`/docs/${currentArticle.path}`);
        if (response.ok) {
          const text = await response.text();
          setCurrentMarkdown(text);
        }
      } catch (error) {
        console.error('Error loading markdown for TOC:', error);
      }
    };

    loadMarkdown();
  }, [currentArticle]);

  // Get related articles (articles with similar tags)
  const getRelatedArticles = (article: Article, category: Category) => {
    if (!metadata) return [];

    const related: Array<{ category: string; article: Article }> = [];

    metadata.categories.forEach(cat => {
      cat.articles.forEach(art => {
        if (art.id !== article.id) {
          const commonTags = art.tags.filter(tag => article.tags.includes(tag));
          if (commonTags.length > 0) {
            related.push({ category: cat.id, article: art });
          }
        }
      });
    });

    return related.slice(0, 3); // Return top 3 related articles
  };

  const handleToggleMobile = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleCategoryClick = (category: Category) => {
    const basePath = category.requiresPermission ? '/docs/privileged' : '/docs';
    // Navigate to first article in category
    if (category.articles.length > 0) {
      navigate(`${basePath}/${category.id}/${category.articles[0].id}`);
    }
  };

  if (!metadata) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading documentation...</Typography>
      </Box>
    );
  }

  // Generate SEO metadata
  const getSEOData = () => {
    if (currentArticle && currentCategory) {
      return {
        title: `${currentArticle.title} - ${currentCategory.title} | HTL MarketCompass Documentation`,
        description: currentArticle.description || `Learn about ${currentArticle.title} in our comprehensive documentation for HTL MarketCompass hotel market intelligence platform.`,
        canonical: `/docs/${categoryId}/${articleId}`
      };
    }
    return {
      title: 'Documentation - HTL MarketCompass',
      description: 'Comprehensive documentation for HTL MarketCompass hotel market intelligence platform. Learn how to access market reports, analyze hotel performance data, and maximize your subscription.',
      canonical: '/docs'
    };
  };

  const seoData = getSEOData();

  // Show home page if no article selected
  if (!categoryId || !articleId) {
    return (
      <>
      <SEO
        title={seoData.title}
        description={seoData.description}
        canonical={seoData.canonical}
      />
      <Box>
        {/* Header */}
        <Box
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            py: 8,
            mb: 4
          }}
        >
          <Container maxWidth="lg">
            <Typography variant="h2" component="h1" gutterBottom fontWeight={700}>
              How can we help you?
            </Typography>
            <Typography variant="h6" paragraph>
              Search our documentation or browse by category
            </Typography>
            <TextField
              fullWidth
              placeholder="Search documentation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                backgroundColor: 'white',
                borderRadius: 1,
                maxWidth: 600,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'transparent'
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Container>
        </Box>

        {/* Category Grid */}
        <Container maxWidth="lg" sx={{ pb: 8 }}>
          <Typography variant="h4" gutterBottom fontWeight={600} sx={{ mb: 4 }}>
            Browse by Category
          </Typography>
          <Grid container spacing={3}>
            {visibleCategories.map(category => {
              const IconComponent = iconMap[category.icon] || Help;
              return (
                <Grid item xs={12} sm={6} md={4} key={category.id}>
                  <Card
                    elevation={2}
                    sx={{
                      height: '100%',
                      transition: 'all 0.2s',
                      '&:hover': {
                        elevation: 6,
                        transform: 'translateY(-4px)'
                      }
                    }}
                  >
                    <CardActionArea
                      onClick={() => handleCategoryClick(category)}
                      sx={{ height: '100%', p: 3 }}
                    >
                      <Box display="flex" alignItems="center" mb={2}>
                        <IconComponent
                          sx={{
                            fontSize: 40,
                            color: 'primary.main',
                            mr: 2
                          }}
                        />
                        <Typography variant="h6" fontWeight={600}>
                          {category.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {category.description}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 2, display: 'block' }}
                      >
                        {category.articles.length} articles
                      </Typography>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Popular Articles Section */}
          <Box mt={6}>
            <Typography variant="h4" gutterBottom fontWeight={600} sx={{ mb: 3 }}>
              Popular Articles
            </Typography>
            <Grid container spacing={2}>
              {visibleCategories.slice(0, 2).map(category =>
                category.articles.slice(0, 2).map(article => (
                  <Grid item xs={12} sm={6} key={article.id}>
                    <Paper
                      elevation={1}
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          elevation: 3,
                          backgroundColor: 'action.hover'
                        }
                      }}
                      onClick={() => {
                        const basePath = category.requiresPermission ? '/docs/privileged' : '/docs';
                        navigate(`${basePath}/${category.id}/${article.id}`);
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={600} color="primary">
                        {article.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {article.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))
              )}
            </Grid>
          </Box>
        </Container>
      </Box>
      </>
    );
  }

  // Show article page
  return (
    <>
    <SEO
      title={seoData.title}
      description={seoData.description}
      canonical={seoData.canonical}
    />
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Mobile menu button */}
      {isMobile && (
        <IconButton
          onClick={handleToggleMobile}
          sx={{
            position: 'fixed',
            top: 80,
            left: 16,
            zIndex: 1200,
            backgroundColor: 'background.paper',
            boxShadow: 2
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* Sidebar */}
      <DocsSidebar
        categories={metadata.categories}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: { md: `calc(100% - 640px)` }
        }}
      >
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            component={RouterLink}
            to="/docs"
            underline="hover"
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            Documentation
          </Link>
          {currentCategory && (
            <Link
              component={RouterLink}
              to={`/docs/${currentCategory.id}`}
              underline="hover"
              color="inherit"
            >
              {currentCategory.title}
            </Link>
          )}
          {currentArticle && (
            <Typography color="text.primary">{currentArticle.title}</Typography>
          )}
        </Breadcrumbs>

        {/* Article Content */}
        {currentArticle && currentCategory ? (
          <DocArticle
            article={currentArticle}
            category={currentCategory}
            relatedArticles={getRelatedArticles(currentArticle, currentCategory)}
          />
        ) : (
          <Paper sx={{ p: 4 }}>
            <Typography variant="h5">Article not found</Typography>
            <Typography color="text.secondary">
              The requested article could not be found.
            </Typography>
          </Paper>
        )}
      </Box>

      {/* Table of Contents */}
      {currentArticle && <DocTableOfContents markdown={currentMarkdown} />}
    </Box>
    </>
  );
};
